import {
  CATEGORY_TYPE,
  type CategoryType,
} from '@features/buyer/hire/job-price-calculator/priceCalculator';
import {
  FEATURE_FLAG_KEYS,
  isNonAutomotiveCategoryEnabled,
  useFeatureFlag,
} from '@lib/featureFlags';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import i18n from '@translations/i18n';
import { type CSSProperties, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CategoryTypeOption,
  PickerCategory,
} from './categoryPickersTypes';

const pickerDialogSx = {
  '& .MuiDialog-paper': {
    background: 'rgb(var(--card))',
    border: '1px solid rgb(var(--line))',
    borderRadius: '12px',
    minWidth: '300px',
    maxWidth: '420px',
    width: '100%',
    margin: '16px',
  },
};

const pickerTitleSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '1px',
  textTransform: 'uppercase' as const,
  color: 'rgb(var(--muted))',
  borderBottom: '1px solid rgb(var(--line))',
  pb: 1.5,
  pt: 2,
};

const pickerOptionStyle: CSSProperties = {
  padding: '14px 20px',
  borderBottom: '1px solid rgb(var(--line))',
  color: 'rgb(var(--muted))',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'background 0.15s ease, color 0.15s ease',
};

export const getCategoryTypeOptions = (): CategoryTypeOption[] => {
  const options: CategoryTypeOption[] = [
    { value: CATEGORY_TYPE.AUTOMOTIVE, label: i18n.t('hire.automotive') },
  ];
  if (isNonAutomotiveCategoryEnabled()) {
    options.push({
      value: CATEGORY_TYPE.NON_AUTOMOTIVE,
      label: i18n.t('hire.nonAutomotive'),
    });
  }
  return options;
};

const highlightPickerOption = (event: MouseEvent<HTMLDivElement>): void => {
  event.currentTarget.style.background = 'rgb(var(--field))';
  event.currentTarget.style.color = 'rgb(var(--ink))';
};

const resetPickerOption = (event: MouseEvent<HTMLDivElement>): void => {
  event.currentTarget.style.background = '';
  event.currentTarget.style.color = 'rgb(var(--muted))';
};

export type CategoryPickersProps = {
  open: {
    type: boolean;
    category: boolean;
    subCategory: boolean;
  };
  onOpenChange: {
    type: (open: boolean) => void;
    category: (open: boolean) => void;
    subCategory: (open: boolean) => void;
  };
  selectedCategoryType: CategoryType | null;
  categories: PickerCategory[];
  selectedCategory: PickerCategory | null;
  onSelectType: (type: CategoryType) => void;
  onSelectCategory: (category: PickerCategory) => void;
  onSelectSubCategory: (subCategory: {
    id: string | number;
    name?: string;
  }) => void;
};

export function CategoryPickers({
  open,
  onOpenChange,
  selectedCategoryType,
  categories,
  selectedCategory,
  onSelectType,
  onSelectCategory,
  onSelectSubCategory,
}: CategoryPickersProps): JSX.Element {
  const { t } = useTranslation();
  useFeatureFlag(FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY);
  return (
    <>
      <Dialog
        open={open.type}
        onClose={() => onOpenChange.type(false)}
        sx={pickerDialogSx}
      >
        <DialogTitle sx={pickerTitleSx}>{t('hire.selectType')}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {getCategoryTypeOptions().map((typeOption) => (
            <div
              key={typeOption.value}
              style={pickerOptionStyle}
              onClick={() => onSelectType(typeOption.value)}
              onMouseEnter={highlightPickerOption}
              onMouseLeave={resetPickerOption}
            >
              {typeOption.label}
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog
        open={open.category}
        onClose={() => onOpenChange.category(false)}
        sx={pickerDialogSx}
      >
        <DialogTitle sx={pickerTitleSx}>
          {selectedCategoryType === CATEGORY_TYPE.NON_AUTOMOTIVE
            ? t('hire.selectCategory')
            : t('hire.selectVehicleCategory')}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {categories.map((category) => (
            <div
              key={category.id}
              style={pickerOptionStyle}
              onClick={() => onSelectCategory(category)}
              onMouseEnter={highlightPickerOption}
              onMouseLeave={resetPickerOption}
            >
              {category.name}
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog
        open={open.subCategory}
        onClose={() => onOpenChange.subCategory(false)}
        sx={pickerDialogSx}
      >
        <DialogTitle sx={pickerTitleSx}>
          {t('hire.selectSubcategory')}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedCategory?.subCategories?.map((sub) => (
            <div
              key={sub.id}
              style={pickerOptionStyle}
              onClick={() => onSelectSubCategory(sub)}
              onMouseEnter={highlightPickerOption}
              onMouseLeave={resetPickerOption}
            >
              {sub.name}
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
}
